import { FacetFields } from "models/facet-fields";
import React, { useEffect, useRef, useState } from "react";
import BaseSearchBar from "./base-search-bar";
import NTSearchBar from "./nt-search-bar";
import { useNavigate, useSearchParams } from "react-router-dom";
import Input from "models/input/input";
import { Toaster } from "ui-widgets";
import SolrService from "services/SolrService";

const { solrService } = new SolrService();

const SearchBar = ({
  facets,
  forceDefault,
}: {
  facets: FacetFields;
  forceDefault?: boolean;
}) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const abortRef = useRef<AbortController>();

  const [open, setOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>(
    searchParams.get("search_term") || "",
  );

  const [inputsList, setInputsList] = useState<Input[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!searchTerm) return;

    const handler = setTimeout(() => {
      fetchSearchResults();
    }, 300); // Debounce by 300ms

    return () => clearTimeout(handler);
  }, [searchTerm]);

  const fetchSearchResults = () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);
    solrService
      .list<Input>(controller.signal, {
        order_by: { column_name: "updated_at", order: "desc" },
        page_num: 0,
        page_rows: 10,
        search_term: searchTerm ?? "",
      })
      .then((res) => setInputsList(res.data))
      .catch((err) => {
        if (err.name !== "AbortError") {
          console.error(err);
          Toaster.makeToast(err?.message || "Error searching inputs!", "error");
        }
      })
      .finally(() => setIsLoading(false));
  };

  const handleSearch = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("search_term", searchTerm ?? "");
    navigate(`/search?${newParams.toString()}`);
  };

  return !forceDefault &&
    ["FAF-nt"].includes(process.env.REACT_APP_STYLING_THEME ?? "") ? (
    <NTSearchBar
      facets={facets}
      isLoading={isLoading}
      searchTerm={searchTerm}
      inputsList={inputsList}
      handleSearch={handleSearch}
      handleSearchTerm={(newSearchTerm) => setSearchTerm(newSearchTerm)}
      open={open}
      handleOpen={(newOpen) => setOpen(newOpen)}
    />
  ) : (
    <BaseSearchBar
      facets={facets}
      isLoading={isLoading}
      searchTerm={searchTerm}
      inputsList={inputsList}
      handleSearch={handleSearch}
      handleSearchTerm={(newSearchTerm) => setSearchTerm(newSearchTerm)}
      open={open}
      handleOpen={(newOpen) => setOpen(newOpen)}
    />
  );
};

export default SearchBar;
